/**
 * 用戶偏好設定服務
 * 負責組織選擇、案場過濾器等偏好的讀寫
 */

import { supabase } from '@shared/services/core/supabaseClient'

const DEFAULT_FILTER = '監控案場'
const DEFAULT_CITY = '全部地區'

// ============ 內部工具 ============

/** 查詢用戶（先查 id，再查 supabase_user_id） */
async function findUserRow(userId: string): Promise<{ id: string } | null> {
  const { data: userById, error: err1 } = await supabase
    .from('users')
    .select('id')
    .eq('id', userId)
    .maybeSingle()

  if (err1 && err1.code !== 'PGRST116') {
    console.error('[UserPreferences] 查詢用戶失敗 (by id):', err1)
  }
  if (userById) return userById

  const { data: userBySupa, error: err2 } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_user_id', userId)
    .maybeSingle()

  if (err2 && err2.code !== 'PGRST116') {
    console.error('[UserPreferences] 查詢用戶失敗 (by supabase_user_id):', err2)
  }

  return userBySupa ?? null
}

// ============ 組織選擇 ============

export async function saveLastSelectedOrganization(userId: string, orgId: string): Promise<void> {
  try {
    if (!userId || !orgId) {
      console.warn('[UserPreferences] 缺少必要的參數 userId 或 orgId')
      return
    }

    const existingUser = await findUserRow(userId)

    const now = new Date().toISOString()
    const updateData = {
      last_selected_org_id: orgId,
      last_org_update_at: now,
      updated_at: now
    }

    if (existingUser) {
      const { error: updateError } = await supabase.from('users').update(updateData).eq('id', existingUser.id)
      if (updateError) {
        console.error('[UserPreferences] 更新用戶記錄失敗:', updateError)
        throw updateError
      }
    } else {
      const { error: upsertError } = await supabase.from('users').upsert(
        {
          id: userId,
          last_selected_org_id: orgId,
          last_org_update_at: now,
          created_at: now,
          updated_at: now
        },
        { onConflict: 'id' }
      )
      if (upsertError) {
        console.error('[UserPreferences] 新增/更新用戶記錄失敗:', upsertError)
        throw upsertError
      }
    }
  } catch (error) {
    console.error('[UserPreferences] 儲存上次組織選擇失敗:', error)
  }
}

export async function getLastSelectedOrganization(userId: string): Promise<string | null> {
  try {
    if (!userId) {
      console.warn('[UserPreferences] 缺少 userId 參數')
      return null
    }

    const { data: userById, error: err1 } = await supabase
      .from('users')
      .select('last_selected_org_id')
      .eq('id', userId)
      .maybeSingle()

    if (err1 && err1.code !== 'PGRST116') {
      console.warn('[UserPreferences] 查詢用戶失敗 (by id):', err1)
    } else if (userById?.last_selected_org_id) {
      return userById.last_selected_org_id
    }

    const { data: userBySupa, error: err2 } = await supabase
      .from('users')
      .select('last_selected_org_id')
      .eq('supabase_user_id', userId)
      .maybeSingle()

    if (err2 && err2.code !== 'PGRST116') {
      console.warn('[UserPreferences] 查詢用戶失敗 (by supabase_user_id):', err2)
    } else if (userBySupa?.last_selected_org_id) {
      return userBySupa.last_selected_org_id
    }

    return null
  } catch (error) {
    console.error('[UserPreferences] 取得上次組織選擇失敗:', error)
    return null
  }
}

// ============ 過濾器偏好 ============

export async function saveFilterPreferences(userId: string, filter: string, city: string): Promise<void> {
  try {
    if (!userId) {
      console.warn('[UserPreferences] 缺少必要的參數 userId')
      return
    }

    const existingUser = await findUserRow(userId)

    const now = new Date().toISOString()
    const updateData = {
      last_selected_filter: filter || DEFAULT_FILTER,
      last_selected_city: city || DEFAULT_CITY,
      last_filter_update_at: now,
      updated_at: now
    }

    if (existingUser) {
      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', existingUser.id)

      if (updateError) {
        console.error('[UserPreferences] 更新過濾器偏好設定失敗:', updateError)
        throw updateError
      }
    } else {
      const { error: upsertError } = await supabase.from('users').upsert(
        {
          id: userId,
          ...updateData,
          created_at: now
        },
        { onConflict: 'id' }
      )
      if (upsertError) {
        console.error('[UserPreferences] 新增過濾器偏好設定失敗:', upsertError)
        throw upsertError
      }
    }
  } catch (error) {
    console.error('[UserPreferences] 儲存過濾器偏好設定失敗:', error)
  }
}

export async function getFilterPreferences(userId: string): Promise<{ filter: string; city: string } | null> {
  try {
    if (!userId) return null

    const { data: userById, error: err1 } = await supabase
      .from('users')
      .select('last_selected_filter, last_selected_city')
      .eq('id', userId)
      .maybeSingle()

    if (err1 && err1.code !== 'PGRST116') {
      console.error('[UserPreferences] 查詢過濾器偏好失敗 (by id):', err1)
    }

    if (userById) {
      return {
        filter: userById.last_selected_filter || DEFAULT_FILTER,
        city: userById.last_selected_city || DEFAULT_CITY
      }
    }

    const { data: userBySupa, error: err2 } = await supabase
      .from('users')
      .select('last_selected_filter, last_selected_city')
      .eq('supabase_user_id', userId)
      .maybeSingle()

    if (err2 && err2.code !== 'PGRST116') {
      console.error('[UserPreferences] 查詢過濾器偏好失敗 (by supabase_user_id):', err2)
    }

    if (userBySupa) {
      return {
        filter: userBySupa.last_selected_filter || DEFAULT_FILTER,
        city: userBySupa.last_selected_city || DEFAULT_CITY
      }
    }

    return null
  } catch (error) {
    console.error('[UserPreferences] 取得過濾器偏好設定失敗:', error)
    return null
  }
}
